// Usage by person: pure grouping of the Sessions page's two inventories by the member who had each chat.
// Nothing here renders or calls the API, so every number the panel and the person card show can be tested
// without mounting them.
//
// A dashboard chat's id carries its owner's opaque key (agentChat.ts mints `cdash2--<ownerKey>--…`). The
// Alloy people route maps keys to members. A Slack conversation is attributed to the person who started it
// when the Slack people route could name them: to the member with the same email, or to a Slack-only person
// who is not on the dashboard team. Everything else (unnamed Slack users, scheduled jobs, direct API calls)
// stays in its own group. Usage the provider could not tie to any conversation is reported apart from all.

import { mergeSessions, usageFields } from "@/components/sessions/sessionData";

// Exact producer grammar verified in server/agentChat.ts (SESSION_PATTERN). Display classification only.
export const DASHBOARD_SESSION = /^cdash2--([0-9a-f]{32})--[a-z0-9]{1,12}--[0-9a-f]{24}$/;
export const ownerKeyOf = (id) => DASHBOARD_SESSION.exec(String(id || ""))?.[1] ?? null;

export const SLACK_NOTE_MATCHED = "Slack conversations we could not put a name to.";

export const GROUPS = Object.freeze([
  { key: "Slack", label: "Slack", note: "Conversations in Slack. Not assigned to a person." },
  { key: "Automations", label: "Automations", note: "The agent's scheduled jobs. No person behind them." },
  { key: "Agent/API", label: "Agent / API", note: "Tools and operators using the agent directly." },
]);

const TOKEN_FIELDS = ["requestCount", "inputTokens", "outputTokens", "cachedTokens", "totalTokens"];

/** "austin.norman@example.com" → "Austin Norman": the name a row shows until the roster supplies one. */
export function nameFromEmail(email) {
  const local = String(email || "").split("@")[0].trim();
  if (!local) return "";
  return local.split(/[._-]+/).filter(Boolean).map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(" ");
}

export function displayName(person) {
  return (person?.name || "").trim() || nameFromEmail(person?.email) || "Team member";
}

export function initialsOf(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return (parts[0].charAt(0) + (parts.length > 1 ? parts[parts.length - 1].charAt(0) : "")).toUpperCase();
}

const DAY_KEY = (value) => {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date.toISOString().slice(0, 10) : null;
};

function emptyUsage() {
  return Object.fromEntries(usageFields.map((key) => [key, null]));
}

/** Add a row's known figures to a running total; unknowns are skipped, never counted as zero. */
function addKnown(total, row, fields = usageFields) {
  for (const key of fields) {
    if (row[key] == null) continue;
    total[key] = (total[key] ?? 0) + row[key];
  }
}

function bucket(seed) {
  return {
    ...seed,
    sessions: [],
    totals: emptyUsage(),
    recordedSessions: 0,
    unknownCostSessions: 0,
    knownCostUsd: 0,
    recent: { ...emptyUsage(), sessions: 0 },
    perDay: new Map(),
    lastActiveAt: null,
    share: null,
    channels: { Dashboard: 0, Slack: 0 },
  };
}

function addRow(target, row) {
  target.sessions.push(row);
  if (row.source === "Dashboard" || row.source === "Slack") target.channels[row.source] += 1;
  addKnown(target.totals, row, TOKEN_FIELDS);
  if (row.knownCostChunks > 0 && row.knownActualCostUsd != null) {
    target.knownCostUsd += row.knownActualCostUsd;
    target.recordedSessions += 1;
  } else {
    target.unknownCostSessions += 1;
  }
  const when = row.updatedAt || row.startedAt || null;
  if (when && (!target.lastActiveAt || when > target.lastActiveAt)) target.lastActiveAt = when;
  const day = DAY_KEY(row.startedAt || row.updatedAt);
  if (day) target.perDay.set(day, (target.perDay.get(day) ?? 0) + 1);
}

function finish(target, workspaceKnownCostUsd) {
  target.sessions.sort((a, b) => (b.updatedAt || b.startedAt || "").localeCompare(a.updatedAt || a.startedAt || "") || a.id.localeCompare(b.id));
  target.totals.actualCostUsd = target.recordedSessions ? target.knownCostUsd : null;
  target.share = workspaceKnownCostUsd > 0 && target.recordedSessions ? (target.knownCostUsd / workspaceKnownCostUsd) * 100 : null;
  target.conversationsPerDay = [...target.perDay.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([date, count]) => ({ date, count }));
  delete target.perDay;
  return target;
}

const SLACK_USER = /^[UW][A-Z0-9]{2,31}$/;

/** The Slack people route's answer, reduced to what grouping needs; null when Slack stays one group. */
function slackDirectory(slack) {
  if (!slack || slack.available !== true || !Array.isArray(slack.people) || !slack.sessions || typeof slack.sessions !== "object") return null;
  const people = new Map();
  for (const entry of slack.people) {
    if (!entry || !SLACK_USER.test(String(entry.slackUserId)) || typeof entry.name !== "string" || !entry.name.trim()) continue;
    people.set(entry.slackUserId, { slackUserId: entry.slackUserId, name: entry.name.trim(), ownerKey: typeof entry.ownerKey === "string" ? entry.ownerKey : null });
  }
  return { people, sessions: slack.sessions, complete: slack.complete !== false };
}

/**
 * Group the loaded conversations by person.
 *   providerPages: pages from getAgentConversations (recorded usage, 31-day chunks newest first)
 *   historyPages:  pages from getAgentConversationHistory (titles, dates, sources)
 *   people:        the Alloy people route's members
 *   slack:         the Slack people route's answer, or null
 * Returns the members (every member, with or without conversations), Slack people who are not on the team,
 * dashboard chats whose key matches no member, the three unassigned groups, usage without a conversation, and
 * the coverage the first page states.
 */
export function groupByPerson({ providerPages = [], historyPages = [], people = [], slack = null } = {}) {
  const rows = mergeSessions(providerPages, historyPages);
  const members = new Map(people.map((person) => [person.ownerKey, bucket({ kind: "person", key: person.ownerKey, person, name: displayName(person) })]));
  const unknown = new Map();
  const slackOnly = new Map();
  const directory = slackDirectory(slack);
  const groups = new Map(GROUPS.map((group) => [group.key, bucket({
    kind: "group",
    key: group.key,
    name: group.label,
    note: group.key === "Slack" && directory ? SLACK_NOTE_MATCHED : group.note,
  })]));
  const targetOf = new Map();

  for (const row of rows) {
    const key = ownerKeyOf(row.id);
    let target = null;
    let entry = row;
    if (key) {
      target = members.get(key);
      if (!target) {
        target = unknown.get(key) ?? bucket({ kind: "unknown", key, name: "Earlier sign-in", person: null });
        unknown.set(key, target);
      }
    } else {
      const slackUserId = directory && Object.hasOwn(directory.sessions, row.id) ? directory.sessions[row.id] : null;
      const slackPerson = slackUserId ? directory.people.get(slackUserId) : null;
      if (slackPerson) {
        // The route only maps Slack conversations, so the row is one even when the agent's history did not load it.
        entry = row.source === "Slack" ? row : { ...row, source: "Slack" };
        target = slackPerson.ownerKey ? members.get(slackPerson.ownerKey) : null;
        if (!target) {
          target = slackOnly.get(slackUserId) ?? bucket({ kind: "slack", key: `slack:${slackUserId}`, name: slackPerson.name, person: null, slackUserId });
          slackOnly.set(slackUserId, target);
        }
      } else {
        target = groups.get(groups.has(row.source) ? row.source : "Agent/API");
      }
    }
    addRow(target, entry);
    targetOf.set(row.id, target);
  }

  // The first provider page is the newest 31-day chunk, so its rows are each conversation's usage inside it.
  for (const item of providerPages[0]?.sessions ?? []) {
    const target = targetOf.get(item.id);
    if (!target || target.kind === "group") continue;
    target.recent.sessions += 1;
    addKnown(target.recent, item);
  }

  const unassigned = { ...emptyUsage(), chunks: 0 };
  for (const page of providerPages) {
    if (!page.unassigned) continue;
    unassigned.chunks += 1;
    addKnown(unassigned, page.unassigned);
  }

  const everyone = [...members.values(), ...slackOnly.values(), ...unknown.values(), ...groups.values()];
  const workspaceKnownCostUsd = everyone.reduce((sum, item) => sum + item.knownCostUsd, 0);
  for (const item of everyone) finish(item, workspaceKnownCostUsd);

  const byUsage = (a, b) => b.knownCostUsd - a.knownCostUsd || b.sessions.length - a.sessions.length || a.name.localeCompare(b.name);
  // A former member who never had a conversation adds nothing to the picture (a workspace collects revoked
  // invitations and test sign-ins over time); one with conversations stays, labelled as a former member.
  const shown = (item) => !(item.person.status === "revoked" && item.sessions.length === 0);
  const first = providerPages[0]?.coverage ?? null;
  return {
    people: [...members.values()].filter(shown).sort(byUsage),
    slackPeople: [...slackOnly.values()].sort(byUsage),
    unknownPeople: [...unknown.values()].sort(byUsage),
    groups: GROUPS.map((group) => groups.get(group.key)),
    unassigned: unassigned.chunks ? unassigned : null,
    workspaceKnownCostUsd,
    loadedSessions: rows.length,
    slack: directory ? { matched: true, complete: directory.complete } : { matched: false, complete: true },
    recentWindow: first ? { start: first.chunkStart, end: first.chunkEnd } : null,
    coverage: first ? { since: first.since, asOf: first.asOf } : null,
  };
}

/** Everyone the By person view can open a card for. */
export function openablePeople(grouped) {
  return [...grouped.people, ...grouped.slackPeople, ...grouped.unknownPeople];
}

/** The By source view: dashboard chats as one figure, then Slack, Automations and Agent / API, by where each started. */
export function bySource(grouped) {
  const dashboard = bucket({ kind: "group", key: "Dashboard", name: "Dashboard chat" });
  const groups = new Map(GROUPS.map((group) => [group.key, bucket({ kind: "group", key: group.key, name: group.label, note: group.note })]));
  for (const item of [...openablePeople(grouped), ...grouped.groups]) {
    for (const row of item.sessions) {
      if (ownerKeyOf(row.id)) addRow(dashboard, row);
      else addRow(groups.get(groups.has(row.source) ? row.source : "Agent/API"), row);
    }
  }
  const sources = [dashboard, ...GROUPS.map((group) => groups.get(group.key))];
  for (const item of sources) finish(item, grouped.workspaceKnownCostUsd);
  return sources;
}

export const percentLabel = (share) => (share == null ? "—" : share > 0 && share < 1 ? "<1%" : `${Math.round(share)}%`);
